import React, { forwardRef } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { useLinkClickHandler, Link as Link$1, NavLink as NavLink$1, matchRoutes, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { d as documentReady } from './shared/vite-react-ssg.a292c181.mjs';
export { C as ClientOnly, H as Head } from './shared/vite-react-ssg.a292c181.mjs';
import { d as deserializeState } from './shared/vite-react-ssg.579feabb.mjs';

const Link = forwardRef((props, ref) => {
  const {
    replace,
    state,
    target,
    preventScrollReset,
    relative,
    to,
    onClick
  } = props;
  const internalOnClick = useLinkClickHandler(to, {
    replace,
    state,
    target,
    preventScrollReset,
    relative
  });
  function handleClick(event) {
    if (onClick)
      onClick(event);
    if (!event.defaultPrevented) {
      React.startTransition(() => {
        internalOnClick(event);
      });
    }
    event.defaultPrevented = true;
    event.preventDefault();
  }
  return /* @__PURE__ */ React.createElement(Link$1, { ...props, ref, onClick: handleClick });
});
Link.displayName = "Link";
const NavLink = forwardRef((props, ref) => {
  const {
    replace,
    state,
    target,
    preventScrollReset,
    relative,
    to,
    onClick
  } = props;
  const internalOnClick = useLinkClickHandler(to, {
    replace,
    state,
    target,
    preventScrollReset,
    relative
  });
  function handleClick(event) {
    if (onClick)
      onClick(event);
    if (!event.defaultPrevented) {
      React.startTransition(() => {
        internalOnClick(event);
      });
    }
    event.defaultPrevented = true;
    event.preventDefault();
  }
  return /* @__PURE__ */ React.createElement(NavLink$1, { ...props, ref, onClick: handleClick });
});
NavLink.displayName = "NavLink";

function ViteReactSSG(routerOptions, fn, options = {}) {
  const {
    transformState,
    rootContainer = "#root",
    ssrWhenDev,
    getStyleCollector = null
  } = options;
  if (process.env.NODE_ENV === "development" && ssrWhenDev !== void 0)
    console.warn("[vite-react-ssg] `ssrWhenDev` option is no longer needed. If you want to use csr, just replace `vite-react-ssg dev` with `vite`.");
  const isClient = typeof window !== "undefined";
  const BASE_URL = routerOptions.basename ?? "/";
  async function createRoot$1(client = false, routePath) {
    const browserRouter = client ? createBrowserRouter(routerOptions.routes, { basename: BASE_URL }) : void 0;
    const appRenderCallbacks = [];
    const onSSRAppRendered = client ? () => {
    } : (cb) => appRenderCallbacks.push(cb);
    const triggerOnSSRAppRendered = () => {
      return Promise.all(appRenderCallbacks.map((cb) => cb()));
    };
    const context = {
      isClient,
      routes: routerOptions.routes,
      router: browserRouter,
      routerOptions,
      onSSRAppRendered,
      triggerOnSSRAppRendered,
      initialState: {},
      transformState,
      routePath,
      base: BASE_URL,
      getStyleCollector
    };
    if (client) {
      await documentReady();
      context.initialState = transformState?.(window.__INITIAL_STATE__ || {}) || deserializeState(window.__INITIAL_STATE__);
    }
    await fn?.(context);
    const initialState = context.initialState;
    return {
      ...context,
      initialState
    };
  }
  if (isClient) {
    (async () => {
      const container = typeof rootContainer === "string" ? document.querySelector(rootContainer) : rootContainer;
      if (!container) {
        if (typeof $jsdom === "undefined")
          console.warn("[vite-react-ssg] Root container not found.");
        return;
      }
      const lazeMatches = matchRoutes(routerOptions.routes, window.location, BASE_URL)?.filter(
        (m) => m.route.lazy
      );
      if (lazeMatches && lazeMatches?.length > 0) {
        await Promise.all(
          lazeMatches.map(async (m) => {
            const routeModule = await m.route.lazy();
            Object.assign(m.route, { ...routeModule, lazy: void 0 });
          })
        );
      }
      const { router } = await createRoot$1(true);
      const app = /* @__PURE__ */ React.createElement(HelmetProvider, null, /* @__PURE__ */ React.createElement(RouterProvider, { router }));
      const isSSR = document.querySelector("[data-server-rendered=true]") !== null;
      if (!isSSR && process.env.NODE_ENV === "development") {
        const root = createRoot(container);
        React.startTransition(() => {
          root.render(app);
        });
      } else {
        React.startTransition(() => {
          hydrateRoot(container, app);
        });
      }
    })();
  }
  return createRoot$1;
}

export { Link, NavLink, ViteReactSSG };
